import { useDispatch, useSelector } from "react-redux"
import { memo, useContext, useState, useEffect, useRef } from "react";
import onScrollIcon from "../../../assets/image/icons/onScroll.png"
import offScrollIcon from "../../../assets/image/icons/offScroll.png"
import './timeline.scss';
import { Legend } from "./common/Legend";
import { timelineHeight_Store } from "../../../store/actions/mainAction";
import { Indicator } from "./common/Indicator";
import { TimeLineContext } from "./timeline.config";
import { ElementsContainer } from './container/ElementsContainer';
import { GetTimelineGridInfo, GetFieldWidth, dragImgDenable } from "./common/commonFunction";
import { SaveZoomRate_Store } from "../../../store/actions/videoPlayAction";
import usePinchZoom from "../../../hooks/usePinchZoom";
import { useVideoController } from "./container/ElementContextMenu/useVideoController";
import { useUploadMedia } from "../tooltap/useUploadMedia";
import { useTheme } from "@mui/material";
import Swal from "sweetalert2";

export const TimelineModule = memo(() => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const TimelineRef = useRef();
  const ElementsRef = useRef();
  const IndicatorRef = useRef();
  const timeContainerRef = useRef();
  const [timelineData, SetTimelineData] = useContext(TimeLineContext);
  const ProjectJSON = useSelector(store => store.projectdata.projectJSON);
  const timelineFit = useSelector(store => store.projectdata.timelineFit);
  const { pinching, delta } = usePinchZoom(TimelineRef);
  const zoomRate = useSelector((store) => store.videodata.zoomRate);
  const isTimelineScroll = useSelector((store) => store.editdata.isTimelineScroll);
  const [totalGroups, setTotalGroups] = useState(0);
  const groupTypeMapRef = useRef(new Map());
  const timelineHeight = useSelector((state) => state.maindata.timelineHeight);
  

  const {deselectItems}= useVideoController();
  const [isDragging, setIsDragging] = useState(false);
  const dragTimeoutRef = useRef(null);
  const {chooseVideoFunc, chooseImageFunc, chooseAudioFunc} = useUploadMedia();

  useEffect(() => {
    if(!isTimelineScroll){
    TimelineRef.current.style.overflowX = 'hidden'
    }else{
      TimelineRef.current.style.overflowX = 'auto'
    }
    
  }, [isTimelineScroll])

  useEffect(() => {
    if (pinching && delta < -30) {
      dispatch(SaveZoomRate_Store(zoomRate - 1));
    }
    if (pinching && delta > 30) {
      dispatch(SaveZoomRate_Store(zoomRate + 1));
    }
  }, [delta, pinching])

  useEffect(() => {
    SetTimelineData({
      ...timelineData,
      timeline: TimelineRef.current,
      elements: ElementsRef.current,
      indicator: IndicatorRef.current,
      container: timeContainerRef.current
    });
  }, []);

 

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (loaded || !ProjectJSON?.duration) return;

    setLoaded(true);
    let zoomRate = getZoomFitValue();
    dispatch(SaveZoomRate_Store(zoomRate));
  }, [ProjectJSON])

  useEffect(() => {
    if (!ProjectJSON?.duration) return;
    let zoomRate = getZoomFitValue();
    dispatch(SaveZoomRate_Store(zoomRate));
  }, [timelineFit])


  //Update timeline height when groups are added or removed
  useEffect(() => {
    if (!ProjectJSON) return;
  
    const mediaTypes = ['videos', 'images', 'audios', 'text'];
    const newGroupTypeMap = new Map();
  
    mediaTypes.forEach((type) => {
      if (ProjectJSON[type]) {
        Object.values(ProjectJSON[type]).forEach((item) => {
          if (!newGroupTypeMap.has(item.groups)) {
            newGroupTypeMap.set(item.groups, type);
          }
        });
      }
    });
  
    const prevGroupTypeMap = groupTypeMapRef.current;
  
    const prevGroups = new Set(prevGroupTypeMap.keys());
    const newGroups = new Set(newGroupTypeMap.keys());
  
    const addedGroups = [...newGroups].filter((group) => !prevGroups.has(group));
    const removedGroups = [...prevGroups].filter((group) => !newGroups.has(group));
  
    let heightChange = 0;
  
    const percentageForVideos = (60 / window.innerHeight) * 100;  
    const percentageForOtherTypes = (40 / window.innerHeight) * 100;  
  
    if (addedGroups.length > 0) {
      addedGroups.forEach((group) => {
        const type = newGroupTypeMap.get(group);
        heightChange -= type === 'videos' ? percentageForVideos : percentageForOtherTypes;
      });
    }
  
    if (removedGroups.length > 0) {
      removedGroups.forEach((group) => {
        const type = prevGroupTypeMap.get(group);
        heightChange += type === 'videos' ? percentageForVideos : percentageForOtherTypes;
      });
    }
  
    if (heightChange !== 0) {
      const currentHeightPercentage = parseFloat(timelineHeight);
      let newHeight = currentHeightPercentage + heightChange;
    
      // Limit the height between 40% and 75%
      const MIN_HEIGHT = 45;
      const MAX_HEIGHT = 75;
    
      newHeight = Math.max(MIN_HEIGHT, Math.min(newHeight, MAX_HEIGHT));
    
      dispatch(timelineHeight_Store(newHeight));
    }
    
  
    groupTypeMapRef.current = newGroupTypeMap;
    setTotalGroups(newGroupTypeMap.size);
  
  }, [timelineData, ProjectJSON, timelineHeight]);
  


  const getZoomFitValue = () => {
    let fitZoomRate = 0;
    let tempLegend = [], startLeft = -1;
    let duration = ProjectJSON?.duration;
    let tempArray = new Array(21).fill(0);

    if (duration === 0) return fitZoomRate;
    tempArray.forEach((v, key) => {
      let zoom = key - 10;
      const legendInfo = GetTimelineGridInfo(zoom);
      const { SecondW, startL } = GetFieldWidth(legendInfo);
      let tempData = { zoom: zoom, SecondW: SecondW }
      if (startLeft === -1) startLeft = startL;
      tempLegend.unshift(tempData);
    })

    let totalWidth = TimelineRef.current.offsetWidth - startLeft;
    let activeItem = tempLegend.find((legend) => { return legend.SecondW * duration / 1000 <= totalWidth; })
    if (typeof activeItem?.zoom !== 'number') return fitZoomRate;
    fitZoomRate = activeItem?.zoom;
    return fitZoomRate;
  }

  //Drag&drop files
  
  // Manage the drag-over class only when the user actually leaves the timeline
  useEffect(() => {
    if (TimelineRef.current) {
      if (isDragging) {
        TimelineRef.current.classList.add('drag-over');
      } else {
        TimelineRef.current.classList.remove('drag-over');
      }
    }
  }, [isDragging]);

  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragging && e.dataTransfer.types.includes("Files")) {
      setIsDragging(true);
    }
    // Clear any existing timeout
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
  };

  const handleDragLeave = (e) => {
    if (e.relatedTarget && TimelineRef.current.contains(e.relatedTarget)) {
      return; // Ignore if the user is still inside the timeline
    }
    
    dragTimeoutRef.current = setTimeout(() => {
      setIsDragging(false);
    }, 100); // Small delay to prevent flickering
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false); // Remove the drag-over class
  
    // Check if the drop contains a file and not an internal drag event (like moving timeline items)
    if (!e.dataTransfer.types.includes("Files")) {
      return; // Ignore non-file drags (e.g., timeline elements)
    }
  
    // Ensure only one file is dropped
    if (!e.dataTransfer.files || e.dataTransfer.files.length !== 1) {
      Swal.fire({
        position: "center",
        icon: "warning",
        title: "Please drop only one file at a time!",
        showConfirmButton: true,
        confirmButtonText: "Ok",
      });
      return;
    }
  
    const file = e.dataTransfer.files[0]; // Get the first (and only) file
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
  
    // Allowed file types
    const videoFormats = ["mp4", "m4v", "webm", "mov"];
    const imageFormats = ["jpg", "jpeg", "png", "svg", "gif"];
    const audioFormats = ["mp3", "mpeg", "wav"];
  
    if (videoFormats.includes(fileExtension)) {
      chooseVideoFunc({ target: { files: [file] } });
    } else if (imageFormats.includes(fileExtension)) {
      chooseImageFunc({ target: { files: [file] } });
    } else if (audioFormats.includes(fileExtension)) {
      chooseAudioFunc({ target: { files: [file] } });
    } else {
      Swal.fire({
        position: "center",
        icon: "warning",
        title: "Unsupported file format! Supported types:\nMP4, M4V, WEBM, MOV\nJPG, JPEG, PNG, SVG, GIF\nMP3, MPEG, WAV",
        showConfirmButton: true,
        confirmButtonText: "Ok",
      });
    }
  };
  

  return (
    <>
      <div
        className={`v-timeline-container ${theme.palette.mode === 'dark' ? 'dark' : 'light'}`} 
        ref={TimelineRef} 
        onContextMenu={(e) => e.preventDefault()} 
        onClick={deselectItems}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
      >
        <Indicator ref={IndicatorRef} />

        <div className="timeline-elements-wrapper" ref={ElementsRef}>
          <Legend />
          <div className="timeline-elements-container" ref={timeContainerRef}>
            <ElementsContainer type='videos' />
            <ElementsContainer type='images' />
            <ElementsContainer type='text' />
            <ElementsContainer type='audios' />
          </div>
        </div>
      </div>
    </>

  );
});